import React from "react";
import { Download, FileText, Image, Paperclip } from "lucide-react";
import { formatFileSize } from "@/lib/utils";

/**
 * Returns a file icon element based on the MIME type.
 * @param fileType - The MIME type string
 * @returns A lucide icon element
 */
const getFileIcon = (fileType: string | undefined) => {
  if (fileType?.startsWith("image/")) {
    return <Image className="h-3.5 w-3.5 shrink-0" />;
  }
  if (fileType?.startsWith("text/") || fileType?.includes("pdf")) {
    return <FileText className="h-3.5 w-3.5 shrink-0" />;
  }
  return <Paperclip className="h-3.5 w-3.5 shrink-0" />;
};

/** Minimal asset shape needed by AssetChip — compatible with both Asset and OutputAsset. */
interface AssetChipAsset {
  id: string;
  fileName: string;
  fileSize?: number;
  fileType?: string;
}

interface AssetChipProps {
  asset: AssetChipAsset;
  onClick: (asset: AssetChipAsset) => void;
}

/**
 * Renders an asset as a clickable chip with file icon, name, optional size, and download indicator.
 * @param asset - The OutputAsset to display
 * @param onClick - Callback when the chip is clicked
 */
export const AssetChip: React.FC<AssetChipProps> = ({ asset, onClick }) => {
  return (
    <button
      type="button"
      className="group/asset inline-flex items-center gap-1.5 bg-muted hover:bg-muted/80 px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      onClick={() => onClick(asset)}
      title={asset.fileName}
      aria-label={`Download ${asset.fileName}`}
    >
      {getFileIcon(asset.fileType)}
      <span className="truncate max-w-[200px]">{asset.fileName}</span>
      {asset.fileSize !== undefined && (
        <span className="text-muted-foreground/60">
          {formatFileSize(asset.fileSize)}
        </span>
      )}
      <Download className="h-3 w-3 shrink-0 opacity-0 group-hover/asset:opacity-60 transition-opacity" />
    </button>
  );
};
